// src/data/exportHelpers.js
//
// Ticket 19: study-scoped CSV export of all responses.
//
// One CSV row per (response × question) pair. The export joins three
// collections:
//
//   - responses   (source of truth for answer values)
//   - papers      (for paper_title)
//   - memberships (for reviewer_email + reviewer_name)
//   - questionnaires/{studyId} (for question_text + question_type)
//
// The questionnaire tree may have changed since a response was written;
// we mark rows whose questionId no longer exists in the current
// questionnaire with question_deleted=true and leave question_text /
// question_type empty.

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { loadQuestionnaire, getAllLeafQuestions } from './questionnaireHelpers';
import { loadAllPapersForStudy } from './papersHelpers';
import { listMembershipsForOrg } from '../auth/membershipHelpers';

const CSV_COLUMNS = [
  'response_id',
  'org_id',
  'study_id',
  'paper_id',
  'paper_title',
  'reviewer_id',
  'reviewer_email',
  'reviewer_name',
  'question_id',
  'question_text',
  'question_type',
  'question_deleted',
  'response_value',
  'updated_at',
  'submitted',
  'submitted_at',
];

/**
 * Escapes a single CSV cell. Wraps in quotes and doubles embedded
 * quotes when the value contains a comma, quote, or newline. Returns
 * an empty string for null/undefined.
 */
function csvCell(val) {
  if (val === null || val === undefined) return '';
  let s;
  if (typeof val === 'object') {
    // Firestore Timestamps
    if (typeof val.toDate === 'function') {
      s = val.toDate().toISOString();
    } else if (Array.isArray(val)) {
      s = val.map((v) => (v === null || v === undefined ? '' : String(v))).join('; ');
    } else {
      s = JSON.stringify(val);
    }
  } else {
    s = String(val);
  }
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function rowToCsvLine(row) {
  return CSV_COLUMNS.map((c) => csvCell(row[c])).join(',');
}

/**
 * Build the CSV text for a given (orgId, studyId). Returns a string
 * that can be dropped into a Blob and downloaded.
 */
export async function buildResponsesCsv(orgId, studyId) {
  if (!orgId || !studyId) {
    throw new Error('buildResponsesCsv requires orgId + studyId');
  }

  const [questionnaire, papers, memberships, responsesSnap] = await Promise.all([
    loadQuestionnaire(studyId),
    loadAllPapersForStudy(studyId),
    listMembershipsForOrg(orgId),
    getDocs(
      query(
        collection(db, 'responses'),
        where('studyId', '==', studyId),
      ),
    ),
  ]);

  // Build lookup maps.
  const questionsById = {};
  if (questionnaire) {
    for (const q of getAllLeafQuestions(questionnaire)) {
      questionsById[q.id] = q;
    }
  }
  const papersById = {};
  for (const p of papers || []) papersById[p.id] = p;
  const membershipByUid = {};
  for (const m of memberships || []) membershipByUid[m.uid] = m;

  const lines = [CSV_COLUMNS.join(',')];
  let rowCount = 0;

  responsesSnap.forEach((docSnap) => {
    const r = docSnap.data();
    const reviewerUid = r.reviewerId || r.userId;
    const membership = membershipByUid[reviewerUid] || null;
    const paper = papersById[r.paperId] || null;
    const answers = r.answers || r.responses || {};

    const base = {
      response_id: docSnap.id,
      org_id: r.orgId || orgId,
      study_id: r.studyId || studyId,
      paper_id: r.paperId || '',
      paper_title: (paper && paper.title) || r.paperTitle || '',
      reviewer_id: reviewerUid || '',
      reviewer_email: (membership && membership.email) || r.userEmail || '',
      reviewer_name: (membership && membership.displayName) || '',
      updated_at: r.lastUpdated || r.updatedAt || null,
      submitted: r.submitted === true ? 'true' : 'false',
      submitted_at: r.submittedAt || null,
    };

    const questionIds = Object.keys(answers);
    if (questionIds.length === 0) {
      // Still emit a single row for the response so empty responses
      // show up in the export (with blank question fields).
      lines.push(
        rowToCsvLine({
          ...base,
          question_id: '',
          question_text: '',
          question_type: '',
          question_deleted: '',
          response_value: '',
        }),
      );
      rowCount += 1;
      return;
    }

    for (const qid of questionIds) {
      const q = questionsById[qid] || null;
      lines.push(
        rowToCsvLine({
          ...base,
          question_id: qid,
          question_text: (q && (q.text || q.title)) || '',
          question_type: (q && q.type) || '',
          question_deleted: q ? 'false' : 'true',
          response_value: answers[qid],
        }),
      );
      rowCount += 1;
    }
  });

  return {
    csv: lines.join('\n') + '\n',
    rowCount,
    responseCount: responsesSnap.size,
  };
}

/**
 * Triggers a browser download of the generated CSV. Call from a
 * React event handler.
 */
export function downloadCsv(filename, csvText) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
